/* PlanChangeDialog — the "are you sure, and here is exactly what happens" step
 * for someone who ALREADY has a subscription and picks a different plan.
 *
 * Nothing changes until they tap the confirm button. On open it asks the
 * backend for a preview (POST /billing/change-plan/preview), which is Stripe's
 * own figure for the charge, and shows it in plain words. The confirm call
 * sends back the preview's prorationDate so the amount actually charged is the
 * amount shown here.
 *
 * Four outcomes, one wording each (amounts in USD, cents from the API):
 *   upgrade          "You'll be charged $X today. Then $Y on <renewal date>."
 *   downgrade        "You keep <plan> until <date>. Then $Y per month. Nothing is charged today."
 *   trial            "No charge today. Your trial still ends <date>, then $Y."
 *   cancel_scheduled "This cancels your scheduled change. Nothing is charged."
 *
 * A plan change never opens Stripe Checkout and never starts a new trial.
 */

import { useEffect, useState } from 'react'
import { JAI, FONTS } from '../landing/landingShared'
import { ErrorBanner, PrimaryButton } from '../AuthFields'
import { PLANS, fmtPrice } from '../../config/plans'
import { billingApi } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { formatDate } from '../../utils/datetime'

const planName = (id) => PLANS.find((p) => p.id === id)?.name || id
const unitOf = (billing) => (billing === 'annual' ? 'year' : 'month')
const money = (cents) => fmtPrice((cents || 0) / 100)

// How long we wait for the webhook to catch our plan up after an immediate
// change. The change itself has already succeeded by then.
const POLL_TRIES = 8
const POLL_MS = 1500
const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

export default function PlanChangeDialog({ plan, billing, onClose, onDone }) {
  const { user, refreshPlan } = useAuth()
  const tz = user?.timezone
  const when = (value) => formatDate(value, tz)

  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null) // { action, effectiveAt, settled }

  // Fetches the preview. `loading` starts true, so the first call needs no
  // "set loading" of its own; a re-fetch (an expired quote) sets it first.
  const fetchPreview = async () => {
    try {
      const { data } = await billingApi.previewPlanChange(plan.id, billing)
      setPreview(data)
      setError('')
    } catch (err) {
      setPreview(null)
      setError(err.response?.data?.error || 'Could not work out this change. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    billingApi.previewPlanChange(plan.id, billing)
      .then(({ data }) => { if (!cancelled) setPreview(data) })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not work out this change. Please try again.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [plan.id, billing])

  const confirm = async () => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const { data } = await billingApi.changePlan(plan.id, billing, preview.prorationDate)

      // Upgrades and trial changes happen now, but our own copy of the plan is
      // updated by Stripe's webhook a moment later. Wait for it so the app
      // doesn't keep showing the old plan.
      let settled = true
      if (data.action === 'upgrade' || data.action === 'trial') {
        settled = false
        for (let i = 0; i < POLL_TRIES && !settled; i += 1) {
          await sleep(POLL_MS)
          try {
            const { data: status } = await billingApi.getStatus()
            const interval = status.interval === 'year' ? 'annual' : 'monthly'
            settled = status.plan === data.plan && interval === data.billing
          } catch { /* keep trying */ }
        }
      }
      await refreshPlan()
      setDone({ action: data.action, effectiveAt: data.effectiveAt, settled })
    } catch (err) {
      const body = err.response?.data
      if (body?.code === 'QUOTE_EXPIRED') {
        // The price quote went stale while this was open: show the fresh one.
        setLoading(true)
        await fetchPreview()
        setNotice('That price had expired, so we have refreshed it. Please review it and confirm again.')
      } else {
        setError(body?.error || 'Something went wrong. Nothing has been changed.')
      }
    } finally {
      setBusy(false)
    }
  }

  // ── Wording ────────────────────────────────────────────────
  const current = preview ? planName(preview.current.plan) : ''
  const target = planName(plan.id)
  const unit = preview ? unitOf(preview.target.billing) : unitOf(billing)

  let headline = `Change to ${target}`
  let lines = []
  let confirmLabel = 'Confirm change'

  if (preview) {
    if (preview.action === 'upgrade') {
      headline = `Upgrade to ${target}`
      lines = [
        preview.chargeToday > 0
          ? <>You'll be charged <strong>{money(preview.chargeToday)} today</strong>. Then <strong>{money(preview.nextChargeAmount)} per {unit}</strong> on <strong>{when(preview.nextChargeAt)}</strong>.</>
          : <>You won't be charged anything today. Then <strong>{money(preview.nextChargeAmount)} per {unit}</strong> from <strong>{when(preview.nextChargeAt)}</strong>.</>,
        preview.creditRemaining > 0
          ? <>The unused time on your current plan, <strong>{money(preview.creditRemaining)}</strong>, stays on your account as credit and is used up on your next bills first.</>
          : null,
        'Your new plan starts straight away.',
      ]
      confirmLabel = preview.chargeToday > 0 ? `Confirm and pay ${money(preview.chargeToday)} now` : 'Confirm upgrade'
    } else if (preview.action === 'downgrade') {
      headline = `Switch to ${target}`
      lines = [
        <>You keep <strong>{current}</strong> until <strong>{when(preview.effectiveAt)}</strong>. Then <strong>{money(preview.nextChargeAmount)} per {unit}</strong>. <strong>Nothing is charged today.</strong></>,
        'Nothing is refunded and nothing changes before then. You can cancel the switch any time before that date.',
      ]
      confirmLabel = `Confirm switch on ${when(preview.effectiveAt)}`
    } else if (preview.action === 'trial') {
      headline = `Change to ${target}`
      lines = [
        <><strong>No charge today.</strong> Your trial still ends <strong>{when(preview.trialEnd)}</strong>, then <strong>{money(preview.nextChargeAmount)} per {unit}</strong>.</>,
        'Changing plan does not start a new trial.',
      ]
    } else if (preview.action === 'cancel_scheduled') {
      headline = 'Keep your current plan'
      lines = [
        <>This cancels your scheduled change{preview.effectiveAt ? <> on <strong>{when(preview.effectiveAt)}</strong></> : null}. You stay on <strong>{current}</strong> and <strong>nothing is charged</strong>.</>,
      ]
      confirmLabel = 'Keep my plan'
    }
    if (preview.replacesScheduledChange) {
      lines.push('This replaces the plan change you scheduled earlier.')
    }
    if (preview.clearsPendingCancellation) {
      lines.push('You had cancelled your subscription. Choosing a plan keeps it going, so it will renew as normal.')
    }
  }

  // ── Layout ─────────────────────────────────────────────────
  const shell = (children) => (
    <div
      role="dialog" aria-modal="true" aria-label="Confirm plan change"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose() }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(10,31,68,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div style={{ width: '100%', maxWidth: 460, background: JAI.white, borderRadius: 16, padding: '26px 24px 22px', boxShadow: '0 24px 60px rgba(10,31,68,0.30)', fontFamily: FONTS.tight }}>
        {children}
      </div>
    </div>
  )

  if (done) {
    const immediate = done.action === 'upgrade' || done.action === 'trial'
    return shell(
      <>
        <h2 style={{ margin: '0 0 10px', fontSize: 21, fontWeight: 800, color: JAI.navy, letterSpacing: '-0.02em' }}>
          {done.action === 'cancel_scheduled' ? 'Your plan stays as it is'
            : immediate ? `You're on ${target}`
            : 'Your plan change is scheduled'}
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.6, color: JAI.mute }}>
          {done.action === 'cancel_scheduled' && 'The scheduled change is cancelled. Nothing was charged.'}
          {done.action === 'downgrade' && <>Your plan changes to <strong>{target}</strong> on <strong>{when(done.effectiveAt)}</strong>. You keep everything in your current plan until then.</>}
          {immediate && (done.settled
            ? 'The change is in place. We have emailed you a confirmation.'
            : 'The change went through. Your plan will finish updating in a moment; refresh if it still shows the old one.')}
        </p>
        <PrimaryButton type="button" onClick={onDone}>Done</PrimaryButton>
      </>
    )
  }

  return shell(
    <>
      <h2 style={{ margin: '0 0 14px', fontSize: 21, fontWeight: 800, color: JAI.navy, letterSpacing: '-0.02em' }}>{headline}</h2>

      {loading && <p style={{ margin: '0 0 18px', fontSize: 14, color: JAI.mute }}>Working out exactly what this will do…</p>}

      {notice && <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.55, color: JAI.blue, fontWeight: 600 }}>{notice}</p>}

      {!loading && preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 0 20px' }}>
          {lines.filter(Boolean).map((line, i) => (
            <p key={i} style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: i === 0 ? JAI.navy : JAI.mute }}>{line}</p>
          ))}
          <p style={{ margin: '4px 0 0', fontSize: 11.5, color: JAI.faint }}>All amounts in USD.</p>
        </div>
      )}

      <ErrorBanner>{error}</ErrorBanner>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {preview && (
          <PrimaryButton type="button" loading={busy || loading} onClick={confirm}>
            {busy ? 'Working…' : confirmLabel}
          </PrimaryButton>
        )}
        <button
          type="button" onClick={onClose} disabled={busy}
          style={{ background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', color: JAI.mute, fontSize: 13.5, fontWeight: 600, fontFamily: FONTS.tight, padding: 8 }}
        >
          {preview ? 'Not now' : 'Close'}
        </button>
      </div>
    </>
  )
}
